import axios from "axios";
import { newIdempotencyKey } from "@/composables/globalMethods";
import { exportDownload } from "@/services/fileExport";

function readState(key) {
    try {
        return JSON.parse(sessionStorage.getItem(key)) || {};
    } catch {
        return {};
    }
}
function saveState(key, state) {
    try {
        sessionStorage.setItem(key, JSON.stringify(state));
    } catch {
        /* Retry still works through the tenant's active-job constraint. */
    }
}
function clearState(key) {
    try {
        sessionStorage.removeItem(key);
    } catch {
        /* Storage is optional. */
    }
}
function wait(signal) {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) return reject(new DOMException("Export paused", "AbortError"));
        const abort = () => {
            clearTimeout(timer);
            reject(new DOMException("Export paused", "AbortError"));
        };
        const timer = setTimeout(() => {
            signal?.removeEventListener("abort", abort);
            resolve();
        }, 2000);
        signal?.addEventListener("abort", abort, { once: true });
    });
}

// Keep the logical request/job through reloads and ambiguous network failures.
// Tenant and authenticated-account identity scope the resume state.
export async function prepareDataExport({ baseUrl, tenant, actor, signal, onProgress = () => {} }) {
    const storageKey = `forkto-export:${tenant}:${actor}`;
    const state = readState(storageKey);
    const currentTenant = () => String(axios.defaults.headers.common["Customer-ID"] || "");
    const assertContext = () => {
        if (signal?.aborted || currentTenant() !== tenant)
            throw new DOMException("Export paused", "AbortError");
    };
    assertContext();
    if (!state.id) {
        state.key ||= newIdempotencyKey();
        saveState(storageKey, state);
        const response = await axios.post(
            `${baseUrl}/exports`,
            {},
            {
                headers: { "Idempotency-Key": state.key },
                signal
            }
        );
        assertContext();
        state.id = response.data.data.id;
        saveState(storageKey, state);
    }
    for (;;) {
        assertContext();
        let response;
        try {
            response = await axios.get(`${baseUrl}/exports/${state.id}`, { signal });
        } catch (error) {
            if (error?.response?.status === 404) clearState(storageKey);
            throw error;
        }
        assertContext();
        const job = response.data.data;
        onProgress({ phase: job.status, progress: job.progress || 0 });
        if (job.status === "failed") {
            clearState(storageKey);
            throw new Error(job.error || "Export preparation failed. Please try again.");
        }
        if (job.status === "ready") {
            onProgress({ phase: "downloading", progress: 100 });
            const result = await exportDownload({
                url: job.download_url,
                filename: job.filename,
                size: job.size,
                signal,
                canContinue: () => currentTenant() === tenant
            });
            // A failed download preserves the ready job for a fresh signed URL.
            clearState(storageKey);
            return { ...result, success: result.mode !== "cancelled", filename: job.filename };
        }
        if (!["queued", "running"].includes(job.status))
            throw new Error("The export status could not be confirmed.");
        await wait(signal);
    }
}
